import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output } from '@angular/core';
import { roleLabel } from '../../chat.format';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import { ChatPresenceStatus } from '../../chat.model';

/**
 * Top of a conversation (80px, `card-header` look): avatar 44px with
 * presence, name + role pill ("Cliente" / "Consultor" / "Equipa"), "Online
 * agora" in green (or the away / owner / participants line), and the square
 * 36px actions on the right: invite (only with `canInvite`), search in the
 * conversation, details (toggles the right panel) and a "more" menu (open the
 * property on the agency site or its CRM page).
 *
 * `showBack` renders the back arrow; the CSS only shows it under 992px, where
 * the list and the conversation take turns on screen, and always inside the
 * floating widget (one column).
 *
 * CRM: `ownerName` (supervised conversation) or `assignedName` (responsible,
 * when it is someone else) and `participantNames` ("com Rui e Ana") are added
 * to the subtitle. While `typing` is true the subtitle becomes three animated
 * dots + "a escrever…".
 */
@Component({
  selector: 'chat-thread-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-thread-header.component.html',
  styleUrls: ['./chat-thread-header.component.css']
})
export class ChatThreadHeaderComponent implements OnChanges {
  @Input() name = '';
  @Input() photo?: string;
  @Input() role?: string;
  @Input() presence: ChatPresenceStatus | null = null;
  @Input() showBack = false;
  @Input() canInvite = false;
  /** Router commands for the property page of the CRM, or null. */
  @Input() propertyRoute: any[] | null = null;
  @Input() propertyQueryParams: { [key: string]: any } | null = null;
  /** Public link of the property on the agency site (back-end `propertyLink`). */
  @Input() propertyLink?: string;
  /** Consultant who owns a supervised conversation. */
  @Input() ownerName?: string;
  /** Responsible for the conversation, when it is not the logged user (v2 `assignedName`). */
  @Input() assignedName?: string;
  /** Other staff in the conversation (invited colleagues), without the logged user. */
  @Input() participantNames: string[] = [];
  /** The other side is typing (chat_typing); `typingMode` 'voice' = recording a voice message. */
  @Input() typing = false;
  @Input() typingMode: 'text' | 'voice' = 'text';
  /** State of the right-hand details panel (aria-pressed of the "Detalhes" button). */
  @Input() detailsOpen = false;
  /** State of the in-conversation search row. */
  @Input() searchOpen = false;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;

  @Output() back = new EventEmitter<void>();
  @Output() invite = new EventEmitter<void>();
  @Output() toggleDetails = new EventEmitter<void>();
  @Output() toggleSearch = new EventEmitter<void>();

  subtitle = '';
  roleText = '';
  menuOpen = false;

  constructor(private host: ElementRef<HTMLElement>) {}

  ngOnChanges(): void {
    this.roleText = roleLabel(this.role, this.strings);
    const presence = this.presence === 'online'
      ? this.strings.onlineNow
      : this.presence === 'away' ? this.strings.presenceAway : this.strings.presenceOffline;
    const owner = this.ownerName
      ? this.strings.ownerLabel.replace('{name}', this.ownerName)
      : this.assignedName ? this.strings.assignedLabel.replace('{name}', this.assignedName) : '';
    const names = (this.participantNames || []).filter(n => !!n);
    const withNames = names.length > 0 ? this.strings.withParticipants.replace('{names}', names.join(', ')) : '';
    this.subtitle = [presence, owner, withNames].filter(p => !!p).join(' · ') || this.roleText;
  }

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.menuOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.menuOpen = false;
  }
}
